import moment from 'moment'
import {useState} from 'react'
import {Modal, Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {NumericFormat} from 'react-number-format'
import {Table} from 'src/feature/bite/component'
import {currency} from 'src/feature/bite/helpers/currency'
import {Success} from 'src/feature/bite/helpers/notification'
import {
  useDecreaseCredit,
  useIncreaseCredit,
  useModifyCredit,
} from 'src/feature/members/context/useMutation'
import {useGetPackageDetail, useGetPackagePaymentList} from 'src/feature/members/context/useQuery'
import {usePackageDetail} from 'src/feature/members/hook/package'
import Swal from 'sweetalert2'

const PackageDetail = ({
  isDetail,
  setIsDetail,
  data,
}: {
  isDetail: any
  setIsDetail: any
  data: any
}) => {
  const intl = useIntl()
  const {isLoading} = useGetPackageDetail(isDetail.packageId)
  const {isLoading: isLoadingPaymentList} = useGetPackagePaymentList(isDetail.packageId)
  const {detail, paymentList, paymentListPaging, paymentListPagingSet} = usePackageDetail()
  const {mutateAsync: decreaseCredit, isLoading: isDecreasing} = useDecreaseCredit({
    packageId: isDetail.packageId,
  })
  const {mutateAsync: increaseCredit, isLoading: isIncreasing} = useIncreaseCredit({
    packageId: isDetail.packageId,
  })
  const {mutateAsync: modifyCredit} = useModifyCredit({packageId: isDetail.packageId})
  const [isModifyCreditModalOpen, setIsModifyCreditModalOpen] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [tempCredit, setTempCredit] = useState<number>(0)

  const handleDecreaseCredit = async (id: string) => {
    const type = detail?.pricingDetail?.subscriptionOptions === 3 ? 'product' : 'subscription'

    Swal.fire({
      title: `Do you want to proceed with deducting this credit from the member's ${type}?`,
      text: 'This action cannot be undone.',
      iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
      customClass: {
        icon: 'text-primary border-none border-0',
        title: 'fs-4',
        container: 'py-0 my-0',
        htmlContainer: 'pt-3 mt-3',
        confirmButton: 'me-10',
        actions: 'd-flex justify-content-between',
      },
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'YES'}),
      cancelButtonText: intl.formatMessage({id: 'NO'}),
      confirmButtonColor: '#5cb85c',
      cancelButtonColor: '#F1416C',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await decreaseCredit({id})
      }
    })
  }

  const handleIncreaseCredit = async (id: string) => {
    Swal.fire({
      title: `Do you want to proceed with this credit update for the member?`,
      text: 'This action cannot be undone.',
      iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
      customClass: {
        icon: 'text-primary border-none border-0',
        title: 'fs-4',
        container: 'py-0 my-0',
        htmlContainer: 'pt-3 mt-3',
        confirmButton: 'me-10',
        actions: 'd-flex justify-content-between',
      },
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'YES'}),
      cancelButtonText: intl.formatMessage({id: 'NO'}),
      confirmButtonColor: '#5cb85c',
      cancelButtonColor: '#F1416C',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await increaseCredit({id})
      }
    })
  }

  const columns = [
    {
      key: 'status',
      dataIndex: 'status',
      title: 'Status',
      render: (text: number) => {
        switch (text) {
          case 1:
            return <span className='badge badge-primary'>Paid</span>
          case 2:
            return <span className='badge badge-warning'>Inactive</span>
          case 3:
            return <span className='badge badge-danger'>Failed</span>
          case 4:
            return <span className='badge badge-success'>Paid</span>
          case 5:
            return <span className='badge badge-danger'>Unpaid</span>
          case 6:
            return <span className='badge badge-warning'>Waiting</span>
          default:
            return <span className='badge badge-light'>-</span>
        }
      },
    },
    {
      key: 'paymentDate',
      dataIndex: 'paymentDate',
      title: intl.formatMessage({id: 'LABEL.PAYMENT.DATE'}),
      render: (text: string) => {
        return <div className=''>{text ? moment.utc(text).format('DD/MM/YYYY HH:mm') : '-'}</div>
      },
    },
    {
      key: 'amount',
      dataIndex: 'amount',
      title: intl.formatMessage({id: 'CHECKOUT.PURCHASE.TABLE.AMOUNT'}),
      customMonetize: true,
    },
    {
      key: 'channel',
      dataIndex: 'channel',
      title: intl.formatMessage({id: 'CHECKOUT.PURCHASE.TABLE.CHANNEL'}),
      render: (text: number) => {
        switch (text) {
          case 1:
            return <span className='text-primary'>Online</span>
          case 2:
            return <span className='text-primary'>In-Store</span>
          case 3:
            return <span className='text-primary'>Both</span>
          default:
            return <span className='text-primary'>Online</span>
        }
      },
    },
  ]

  return (
    <>
      <Modal
        show={isModifyCreditModalOpen}
        onHide={() => {
          if (
            tempCredit ===
            detail?.packageItemList?.find((item: any) => item.id === selectedItemId)?.remainingCount
          ) {
            setSelectedItemId('')
            setTempCredit(0)
            setIsModifyCreditModalOpen(false)
            return
          }

          setIsModifyCreditModalOpen(false)

          Swal.fire({
            title: `You have made changes to the credit. If you exit now, these changes will be discarded. Do you want to continue?`,
            iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
            customClass: {
              icon: 'text-primary border-none border-0',
              title: 'fs-4',
              container: 'py-0 my-0',
              htmlContainer: 'pt-3 mt-3',
              confirmButton: 'me-10',
              actions: 'd-flex justify-content-between',
            },
            showCancelButton: true,
            confirmButtonText: intl.formatMessage({id: 'YES'}),
            cancelButtonText: intl.formatMessage({id: 'NO'}),
            confirmButtonColor: '#5cb85c',
            cancelButtonColor: '#F1416C',
          }).then((result) => {
            if (result.isConfirmed) {
              setSelectedItemId('')
              setTempCredit(0)
              setIsModifyCreditModalOpen(false)
            } else {
              setIsModifyCreditModalOpen(true)
            }
          })
        }}
        centered
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>Please select the service to adjust its credit.</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!selectedItemId && (
            <div className='d-flex flex-column gap-5'>
              <div className='d-flex flex-column gap-5'>
                <div className='d-flex align-items-center gap-10'>
                  {detail?.packageItemList.map((item: any) => (
                    <div className='d-flex flex-column bg-light px-5 py-3 rounded-2 min-w-300px border gap-5'>
                      <div className='d-flex flex-column'>
                        <span className='mb-2 mt-5 text-black fw-bold fs-3'>{item.name}</span>
                        <span className='fs-5'>
                          Total Credit: <span className='fw-semibold'>{item.count}</span>
                        </span>
                        <span className='fs-5'>
                          Remaining Credit:{' '}
                          <span className='fw-semibold'>{item.remainingCount}</span>
                        </span>
                      </div>

                      <div className='d-flex align-items-center gap-3'>
                        <button
                          className='btn btn-primary d-flex align-items-center'
                          onClick={async () => {
                            setSelectedItemId(item.id)
                            setTempCredit(item.remainingCount)
                          }}
                        >
                          <i className='bi bi-arrow-right fs-3 fw-bold' />
                          <span className='ms-2'>Select</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedItemId && (
            <div className='d-flex flex-column gap-5'>
              <div className='d-flex flex-column gap-5'>
                <div className='d-flex flex-column'>
                  <label>Item Name: </label>
                  <span className='fs-5 fw-semibold'>
                    {detail?.packageItemList?.find((item: any) => item.id === selectedItemId)?.name}
                  </span>
                </div>
                <div className='d-flex flex-column'>
                  <label>Total Credit: </label>
                  <span className='fs-5 fw-semibold'>
                    {
                      detail?.packageItemList?.find((item: any) => item.id === selectedItemId)
                        ?.count
                    }
                  </span>
                </div>
                <div className='d-flex flex-column gap-2'>
                  <label>Remaining Credit: </label>
                  <div className='d-flex align-items-center gap-3'>
                    <button
                      className='btn btn-danger btn-sm btn-icon'
                      onClick={() => {
                        if (tempCredit === 0) return
                        setTempCredit((prev: number) => prev - 1)
                      }}
                    >
                      <i className='bi bi-dash fw-bold fs-2x' />
                    </button>

                    <span className='fs-3 fw-semibold bg-light px-5 py-3 rounded-2 border'>
                      {tempCredit}
                    </span>

                    <button
                      className='btn btn-success btn-icon btn-sm'
                      onClick={() => {
                        setTempCredit((prev: number) => prev + 1)
                      }}
                    >
                      <i className='bi bi-plus fw-bold fs-2x' />
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <button
                      className='btn btn-light d-flex align-items-center'
                      onClick={() => {
                        setSelectedItemId('')
                        setTempCredit(0)
                      }}
                    >
                      <i className='bi bi-arrow-left fw-bold' />
                      <span className='ms-2'>Back</span>
                    </button>
                  </div>
                  <div className='d-flex align-items-center gap-3'>
                    <button
                      className='btn btn-primary d-flex align-items-center'
                      onClick={async () => {
                        setIsModifyCreditModalOpen(false)

                        Swal.fire({
                          title: `Do you want to proceed with adjusting the credit?`,
                          html: `You are about to adjust <strong>${tempCredit}</strong> credit${
                            tempCredit > 1 ? 's' : ''
                          } for the <strong>${
                            detail?.packageItemList?.find((item: any) => item.id === selectedItemId)
                              ?.name
                          }</strong> service.<br />This action cannot be undone.`,
                          iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
                          customClass: {
                            icon: 'text-primary border-none border-0',
                            title: 'fs-4',
                            container: 'py-0 my-0',
                            htmlContainer: 'pt-3 mt-3',
                            confirmButton: 'me-10',
                            actions: 'd-flex justify-content-between',
                          },
                          showCancelButton: true,
                          confirmButtonText: intl.formatMessage({id: 'YES'}),
                          cancelButtonText: intl.formatMessage({id: 'NO'}),
                          confirmButtonColor: '#5cb85c',
                          cancelButtonColor: '#F1416C',
                        }).then(async (result) => {
                          if (result.isConfirmed) {
                            await modifyCredit({
                              id: selectedItemId,
                              credit: tempCredit,
                            })
                            Success(intl.formatMessage({id: 'LABEL.SUCCESS'}))
                            setIsModifyCreditModalOpen(false)
                            setSelectedItemId('')
                            setTempCredit(0)
                          } else {
                            setIsModifyCreditModalOpen(true)
                          }
                        })
                      }}
                    >
                      <span className=''>Confirm</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <div className='card'>
        <div className='card-body'>
          <div className='d-flex flex-column gap-5'>
            <div>
              <i
                className='bi bi-arrow-left text-black fs-2x cursor-pointer'
                onClick={() => setIsDetail({status: false})}
              />
            </div>
            <div className='d-flex flex-column gap-5'>
              <div className='w-100 d-flex justify-content-between align-items-center'>
                <h2 className='fw-bold text-black m-0 p-0'>{detail?.packageDetail?.name}</h2>
                {/*   <div>
                  <button
                    className='btn btn-primary'
                    onClick={() => setIsModifyCreditModalOpen(true)}
                  >
                    <span className=''>Adjust Credit</span>
                  </button>
                </div> */}
              </div>
              <h2 className='fw-bold text-black m-0 p-0'>
                {currency(detail?.packageDetail?.price || 0)}
              </h2>
              <div className='d-flex align-items-center gap-10'>
                <span className='text-black'>
                  Start Date: {moment.utc(detail?.packageDetail?.startDate).format('DD/MM/YYYY')}
                </span>
                <span className='text-black'>
                  End Date: {moment.utc(detail?.packageDetail?.endDate).format('DD/MM/YYYY')}
                </span>
              </div>
              <div className='d-flex gap-5 flex-wrap'>
                {detail?.packageItemList.map((item: any) => (
                  <div className='d-flex flex-column justify-content-between bg-light px-5 py-3 rounded-2 min-w-300px border gap-10'>
                    <div className='d-flex flex-column'>
                      <span className='mb-2 mt-5 text-black fw-bold fs-3'>{item.name}</span>
                      <span className='text-primary fs-5'>
                        Total Credit: <span className='fw-semibold'>{item.count}</span>
                      </span>
                    </div>

                    <div className='d-flex align-items-center gap-5'>
                      {isIncreasing ? (
                        <Spinner animation='border' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </Spinner>
                      ) : (
                        <button
                          className='btn btn-success btn-lg btn-icon'
                          disabled={
                            isDecreasing ||
                            isLoading ||
                            isIncreasing ||
                            detail?.packageDetail?.status === 2 ||
                            detail?.packageDetail?.status === 3 ||
                            detail?.packageDetail?.status === 4 ||
                            detail?.packageDetail?.status === 5
                          }
                          onClick={async () => {
                            if (
                              detail?.packageDetail?.status === 2 ||
                              detail?.packageDetail?.status === 3 ||
                              detail?.packageDetail?.status === 4 ||
                              detail?.packageDetail?.status === 5
                            )
                              return
                            await handleIncreaseCredit(item.id)
                          }}
                        >
                          <i className='bi bi-plus fw-bold fs-2x' />
                        </button>
                      )}
                      <div className='d-flex flex-column align-items-center bg-white px-5 py-3 rounded-2 border'>
                        <span className='fs-5 fw-semibold'>Remaining</span>
                        <p className='fs-3 fw-bold p-0 m-0'>{item.remainingCount}</p>
                      </div>

                      {isDecreasing ? (
                        <Spinner animation='border' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </Spinner>
                      ) : (
                        <button
                          className='btn btn-danger btn-lg btn-icon'
                          disabled={
                            isDecreasing ||
                            isLoading ||
                            isIncreasing ||
                            item?.remainingCount === 0 ||
                            detail?.packageDetail?.status === 2 ||
                            detail?.packageDetail?.status === 3 ||
                            detail?.packageDetail?.status === 4 ||
                            detail?.packageDetail?.status === 5
                          }
                          onClick={async () => {
                            if (item?.remainingCount === 0) return
                            if (
                              detail?.packageDetail?.status === 2 ||
                              detail?.packageDetail?.status === 3 ||
                              detail?.packageDetail?.status === 4 ||
                              detail?.packageDetail?.status === 5
                            )
                              return
                            await handleDecreaseCredit(item.id)
                          }}
                        >
                          <i className='bi bi-dash fw-bold fs-2x' />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='card mt-10'>
        <div className='card-body'>
          <h2 className='fw-bold text-black m-0 p-0 fs-3 pb-5'>
            {intl.formatMessage({id: 'LABEL.PRICING.DETAILS'})}
          </h2>
          {detail?.pricingDetail?.subscriptionOptions !== 3 && (
            <div className='row'>
              <div className='col-md-3 d-flex flex-column'>
                <label className='mb-2'>{intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}</label>
                <NumericFormat
                  className='fs-5 fw-semibold w-100'
                  displayType='text'
                  prefix='AED '
                  decimalScale={2}
                  thousandSeparator={true}
                  placeholder='AED 1.00'
                  fixedDecimalScale={true}
                  value={detail?.pricingDetail?.price}
                  style={{width: '25%'}}
                />
              </div>

              <div className='col-md-3 d-flex flex-column'>
                <label className='mb-2'>
                  {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.OPTIONS'})}
                </label>
                <span className='fs-5 fw-semibold'>
                  {detail?.pricingDetail?.subscriptionOptions === 1
                    ? 'Monthly'
                    : detail?.pricingDetail?.subscriptionOptions === 2
                    ? 'Annual'
                    : 'One Time'}
                </span>
              </div>

              <div className='col-md-3 d-flex flex-column'>
                <label className='mb-2'>{intl.formatMessage({id: 'LABEL.COUNT'})}</label>
                <span className='fs-5 fw-semibold'>{detail?.pricingDetail?.count}</span>
              </div>

              <div className='col-md-3 d-flex flex-column'>
                <label className='mb-2'>
                  {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.RECURRING'})}
                </label>
                <NumericFormat
                  className='fs-5 fw-semibold w-100'
                  displayType='text'
                  allowNegative={false}
                  prefix='AED '
                  decimalScale={2}
                  thousandSeparator={true}
                  placeholder='AED 1.00'
                  fixedDecimalScale={true}
                  aria-describedby='basic-addon2'
                  value={(
                    (detail?.pricingDetail?.price || 0) / (detail?.pricingDetail?.count || 1) || 0
                  ).toFixed(2)}
                  style={{width: '25%'}}
                />
              </div>
            </div>
          )}
          <div className='row mt-5'>
            {detail?.pricingDetail?.subscriptionOptions === 3 && (
              <div className='col-md-3 d-flex flex-column'>
                <label className='mb-2'>{intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}</label>
                <NumericFormat
                  className='fs-5 fw-semibold w-100'
                  displayType='text'
                  prefix='AED '
                  decimalScale={2}
                  thousandSeparator={true}
                  placeholder='AED 1.00'
                  fixedDecimalScale={true}
                  value={detail?.pricingDetail?.price}
                  style={{width: '25%'}}
                />
              </div>
            )}
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.TAX.RATE'})}
              </label>
              <NumericFormat
                className='fs-5 fw-semibold w-100'
                displayType='text'
                allowNegative={false}
                suffix=' %'
                decimalScale={2}
                thousandSeparator={true}
                placeholder='AED 1.00'
                fixedDecimalScale={true}
                aria-describedby='basic-addon2'
                value={detail?.pricingDetail?.taxRate}
                style={{width: '25%'}}
              />
            </div>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.PURCHASE.TYPE'})}
              </label>
              <span className='fs-5 fw-semibold'>
                {detail?.pricingDetail?.purchaseType === 1 ? 'Vasco App' : '-'}
              </span>
            </div>
            {detail?.pricingDetail?.subscriptionOptions !== 3 && (
              <div className='col-md-3 d-flex flex-column'>
                <label className='mb-2'>
                  {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.PURCHASE.CANCELLATION.FEE'})}
                </label>
                <NumericFormat
                  className='fs-5 fw-semibold w-100'
                  displayType='text'
                  allowNegative={false}
                  prefix='AED '
                  decimalScale={2}
                  thousandSeparator={true}
                  placeholder='AED 1.00'
                  fixedDecimalScale={true}
                  aria-describedby='basic-addon2'
                  value={detail?.pricingDetail?.cancellationFee || 0}
                  style={{width: '25%'}}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='card mt-10'>
        <div className='card-body'>
          <h2 className='fw-bold text-black m-0 p-0 fs-3 pb-5'>
            {intl.formatMessage({id: 'LABEL.PAYMENT.DETAILS'})}
          </h2>
          <div className='table-responsive'>
            <Table
              columns={columns}
              loading={isLoadingPaymentList}
              dataSource={paymentList?.response?.dataList}
              pagination={{
                current: paymentList?.response?.paging?.currentPageNumber || 1,
                pageSize: paymentList?.response?.paging?.pageSize || 10,
                total: paymentList?.response?.paging?.totalCount || 0,
                onChange: (page: any) => {
                  const {pageSize, current} = page
                  const currentPage = pageSize !== paymentListPaging.pageSize ? 1 : current

                  paymentListPagingSet({
                    ...paymentListPaging,
                    currentPageNumber: currentPage,
                    pageSize: pageSize,
                  })
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default PackageDetail
